import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconButton } from '@mui/material';

import usePushDataLayerEvent from '../../../../utils/google-analytics.util';
import storageService from '../../../../services/storage.service';
import { AssistanceInformation } from '../../../../models/assistance.model';
import useManageStorage from '../../../../hooks/useManageStorage/useManageStorage';
import { theme } from '../../../../global-styles/colors';
import { Heading2Text, Subhead02Text } from '../../../../components/typography/typography';
import AlxButton from '../../../../components/alx-components/button';
import { images } from '../../../../assets/images/images';
import { icons } from '../../../../assets/icons/icons';
import { craneArrivedMutation, getIntervention } from '../../../../api/assistance/assistance';
import { ConfirmationNoticeStyle } from './confirmation-notice-style';

const ConfirmationNotice = () => {
  const { t } = useTranslation(['common', 'auth', 'aia']);
  const { identifier, intervention, entityCode } = useParams<{
    state: string;
    intervention: string;
    identifier: string;
    entityCode: string;
  }>();
  const navigate = useNavigate();
  const { writeToStorage } = useManageStorage();
  const queryClient = useQueryClient();
  const { pushDataLayerEvent } = usePushDataLayerEvent();

  const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);

  const craneArrived = useMutation({
    mutationFn: async (params: { identifier: any; didArrive: boolean }) => {
      if (!intervention) return;
      const aiaInfo = await getIntervention(intervention);
      if (aiaInfo) craneArrivedMutation(params.identifier, aiaInfo.numeroExpediente, intervention, params.didArrive);
    },
  });

  useEffect(() => {
    // aviso-llegada
    pushDataLayerEvent('screen_view', null, 'aviso-llegada');
  }, [pushDataLayerEvent]);

  return (
    <ConfirmationNoticeStyle>
      <div className="go-back">
        <IconButton
          className={'secondary '}
          onClick={() => navigate(`/assistance/${intervention}/${identifier}`)}
          data-cy="back-step-button"
        >
          <img src={icons.arrowLeftWhiteIcon} alt="" />
        </IconButton>
      </div>
      <Heading2Text className="m-24px" as="h1" weight={600} color={theme.neutrals.white}>
        {t('aia:confirmation.notice.title')}
      </Heading2Text>
      <Subhead02Text className="m-24px" color={theme.neutrals.white}>
        {t('aia:confirmation.notice.did-crane-arrive')}
      </Subhead02Text>
      <div className="button-container m-24px">
        <AlxButton
          className="circle hightlight"
          onClick={() => {
            craneArrived.mutate(
              { identifier, didArrive: true },
              {
                onSuccess: async () => {
                  await storageService.removeItem('metaData');
                  navigate(`/confirmation/success/${intervention}/${identifier}/${entityCode}`);
                },
                onError: () => {
                  console.error('something went wrong');
                },
              },
            );
          }}
        >
          {t('aia:confirmation.notice.did-arrive')}
        </AlxButton>
        <AlxButton
          className="circle"
          onClick={() => {
            craneArrived.mutate(
              { identifier, didArrive: false },
              {
                onSuccess: async () => {
                  // dispatch(deleteActiveAssistance({ identifier }));
                  await storageService.removeItem('metaData');
                  await writeToStorage('postponedSurvey', {
                    file: assistanceData?.file,
                    intervention,
                    identifier,
                  });
                  navigate(`/confirmation/error/${intervention}/${identifier}/${entityCode}`);
                },
                onError: () => {
                  console.error('something went wrong');
                },
              },
            );
          }}
        >
          {t('aia:confirmation.notice.did-not-arrive')}
        </AlxButton>
      </div>

      <div className="confirmation-img">
        <img src={images.cars} alt="" />
      </div>
    </ConfirmationNoticeStyle>
  );
};

export default ConfirmationNotice;
